import React, { memo, useContext } from 'react';
import { SortableItemOverlay, SortableItem } from './SortableItem';

import WidgetBase from '../Widgets/WidgetBase';

import SettingsContext from '../../context/SettingsContext';

import { classNames } from '../helpers/functions';
import { areEqualWithIndexPath } from './helpers';
import { useApp } from '../../context/AppContextBeta';

export const CardContent = memo(function ItemContent({
	item,
	isPreview,
	destCtx,
	onForceMove,
	ctx,
	...rest
}) {
	return React.cloneElement(item.children, {
		isPreview,
		listeners: rest?.listeners,
		destCtx,
		onForceMove,
		ctx,
	});
});

const PreviewComponent = ({ actions, title, category, description }) => {
	return (
		<div {...actions} className="w-full">
			<div className="shadow shadow-slate-200 rounded-lg py-4 px-6 cursor-grab select-none text-xs flex flex-col flex-grow flex-1 overflow-auto">
				<div className="flex items-center flex-1">
					{title && (
						<>
							<h4 className="font-medium flex items-baseline flex-col sm:flex-row ">
								{`${category} `}{' '}
								<span className="text-xxs text-gray-400 mx-2">/</span>{' '}
								{`${title}`}
							</h4>
						</>
					)}
				</div>
				<span className="text-xs text-gray-400 font-normal mt-1">
					{description}
				</span>
			</div>
		</div>
	);
};

export const BaseDraggableWidget = ({
	title,
	category,
	description,
	widgetProps,
	children,
	listeners,
	isPreview,
	destCtx,
	onForceMove,
	ctx,
}) => {
	const {
		settings,
		loading: isLoadingSettings,
		update: updateSettings,
	} = useContext(SettingsContext);

	const { isDesktop } = useApp();

	const widgetSettings = settings?.user?.widgetSettings;

	const actions = isDesktop
		? {
				onClick: () => {
					if (typeof onForceMove === 'function') {
						onForceMove(ctx, destCtx, {});
					}
				},
				...listeners,
			}
		: {};

	if (isPreview) {
		return (
			<PreviewComponent
				title={title}
				category={category}
				description={description}
				actions={actions}
			/>
		);
	}

	const removeWidgetMenuItem = [
		{
			name: 'Remove Widget',
			onClick: () => {
				if (typeof onForceMove === 'function') {
					onForceMove({ indexPath: ctx.indexPath }, { indexPath: [1] }, {});
				}
			},
		},
	];

	const { additonalMenuItems: menuItems } = widgetProps || {};

	const additionalMenuItems = [
		...(menuItems?.length ? menuItems : []),
		...removeWidgetMenuItem,
	];

	return (
		<WidgetBase
			title={title}
			titleListeners={actions}
			rows={2}
			fullHeight={true}
			menuItems={isPreview ? null : additionalMenuItems}
			allowResize={['half', 'full']}
			onResize={(size) => {
				// change dashboard to widgetDashboardType before production
				const widgetSettings = settings?.user?.widgetSettings;

				let updatedValue = {};

				if (widgetSettings) {
					if (widgetSettings[ctx?.data?.id]) {
						updatedValue = {
							...widgetSettings,
							[ctx?.data?.id]: { ...widgetSettings[ctx?.data?.id], size },
						};
					} else {
						// settings object exists but not for this widget
						updatedValue = {
							...widgetSettings,
							[ctx?.data?.id]: { size },
						};
					}
				} else {
					// no settings object exists
					updatedValue = {
						[ctx?.data?.id]: { size },
					};
				}

				/*let updatedWidgetSetting = {
					...(settings?.user?.widgetSettings
						? settings?.user?.widgetSettings[ctx?.data?.id]
						: false
						? { ...settings?.user?.widgetSettings[ctx?.data?.id],  }
						: { [ctx?.data?.id]: { size } }),
				};

				/*let updatedValue = {
					...(settings?.user?.widgetSettings
						? { ...settings?.user?.widgetSettings, ...updatedWidgetSetting }
						: { ...updatedWidgetSetting }),
				};*/

				updateSettings({
					type: 'user',
					key: 'widgetSettings',
					value: updatedValue,
				});
			}}
			{...widgetProps}
		>
			{React.cloneElement(children, { isPreview })}
		</WidgetBase>
	);
};

export const NotepadWidget = ({ title, isPreview, ...rest }) => {
	if (isPreview) {
		return (
			<div className="p-4">
				<h2 className="text-md font-light">{title}</h2>
				Testing Preview
			</div>
		);
	}
	return <textarea className="h-48 border border-gray-200 w-full" />;
};

export const SummaryWidget = ({ title, isPreview, ...rest }) => {
	if (isPreview) {
		return (
			<div className="p-4">
				<h2 className="text-md font-light">{title}</h2>
				sum prev
			</div>
		);
	}
	return <div>Sum full</div>;
};

export const SortableCard = memo(function SortableCard({
	isOverlay,
	item,
	itemIndex,
	laneId,
	laneIndexPath,
	defaultPreview,
	destinationLane,
	onForceMove,
	widgetSettings,
}) {
	const ItemElement = isOverlay ? SortableItemOverlay : SortableItem;

	const ctx = {
		parentId: laneId,
		indexPath: [...laneIndexPath, itemIndex],
		data: item,
	};

	const destCtx = {
		parentId: destinationLane?.id,
		indexPath: [0],
		data: destinationLane?.data,
	};

	const settings = widgetSettings ? widgetSettings[ctx?.data?.id] : {};

	const sizeVarients = {
		quarter: `col-span-1 md:col-span-1`,
		half: `col-span-1 md:col-span-2`,
		full: `col-span-1 md:col-span-4`,
	};

	return (
		<ItemElement
			className={classNames(
				settings?.size
					? sizeVarients[settings?.size]
					: item?.data?.size
						? sizeVarients[item?.data?.size]
						: 'col-span-1 md:col-span-2',
				defaultPreview ? 'mb-3' : '',
			)}
			ctx={ctx}
		>
			<CardContent
				item={item}
				isPreview={defaultPreview}
				ctx={ctx}
				destCtx={destCtx}
				onForceMove={onForceMove}
			/>
		</ItemElement>
	);
}, areEqualWithIndexPath);

export const SortableCard2 = memo(function SortableCard({
	isOverlay,
	item,
	itemIndex,
	laneId,
	laneIndexPath,
}) {
	const ItemElement = isOverlay ? SortableItemOverlay : SortableItem;
	const ctx = {
		parentId: laneId,
		indexPath: [...laneIndexPath, itemIndex],
		data: item,
	};

	return (
		<ItemElement className="item" ctx={ctx}>
			{/* <CardContent item={item} /> */}
			<NotepadWidget title={item.data.title} isPreview />
		</ItemElement>
	);
}, areEqualWithIndexPath);
